import * as Popover from '@radix-ui/react-popover'
import { Check, ChevronDown, Search, X } from '@/design-system/icons'
import { forwardRef, useEffect, useId, useImperativeHandle, useMemo, useRef, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react'
import { iconForOption, lookupOfIcon } from '@/services/lookupIcons'
import { iconForLabel } from '../labelIcons'
import { cn, Icon, type IconComponent } from './primitives'
import { ScanButton } from './BarcodeScanner'

const control =
  // the placeholder is always in the UI font: a code box's font-mono made its Arabic hint fall apart letter by letter
  'w-full rounded-[10px] border border-border bg-surface text-sm text-fg placeholder:text-fg-subtle placeholder:font-sans placeholder:tracking-normal shadow-xs transition-[border,box-shadow] duration-150 hover:border-border-strong focus:border-brand focus:shadow-[var(--focus-ring)] focus:outline-none disabled:cursor-not-allowed disabled:bg-bg-subtle disabled:text-fg-subtle aria-[invalid=true]:border-danger'

export interface FieldProps {
  label?: ReactNode
  hint?: ReactNode
  error?: string
  required?: boolean
  className?: string
  /** SVG shown before the label; derived from the label's words when omitted. */
  icon?: IconComponent
  children: (id: string, describedBy: string | undefined) => ReactNode
}

/** Label + control + hint/error wiring with proper aria attributes. */
export function Field({ label, hint, error, required, className, icon, children }: FieldProps) {
  const id = useId()
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined
  const LabelIcon = label ? (icon ?? iconForLabel(label)) : null
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      {label && (
        <label htmlFor={id} className="flex items-center gap-1.5 text-[12.5px] font-semibold text-fg-muted">
          {LabelIcon && <Icon icon={LabelIcon} size={13} className="text-fg-subtle" />}
          <span>{label}</span>
          {required && <span className="text-danger">*</span>}
        </label>
      )}
      {children(id, describedBy)}
      {error ? (
        <p id={`${id}-error`} className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-fg-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: IconComponent
  invalid?: boolean
  suffix?: ReactNode
  /** Adds the camera-scan button inside the box; the scanned barcode is handed here. */
  scan?: (code: string) => void
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ icon, invalid, suffix, scan, className, dir, ...rest }, ref) {
  // an AWB box gets the camera: one tap scans the barcode into the field, same as typing it
  const end = scan ? <>{suffix}<ScanButton onScan={scan} /></> : suffix
  // A code box (dir="ltr") on an Arabic page: empty, it keeps the page's direction so its Arabic placeholder reads
  // right (as Aliens OS's tracking box does); with a value it turns ltr. The icons sit by the page's direction, so such a
  // box keeps the same room on both sides: its text never runs under the icon or the scan button whichever way it reads.
  const ltr = dir === 'ltr'
  const empty = rest.value !== undefined && String(rest.value) === ''
  const room = scan && suffix ? 'px-16' : end ? 'px-9' : icon ? 'px-8' : null
  return (
    <div className="relative flex items-center">
      {icon && <Icon icon={icon} size={15} className="pointer-events-none absolute start-2.5 text-fg-subtle" />}
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        dir={ltr && empty ? undefined : dir}
        className={cn(control, 'h-10 px-3', ltr && room ? room : [icon && 'ps-8', end && 'pe-9', scan && suffix && 'pe-16'], className)}
        {...rest}
      />
      {end && <div className="absolute end-1.5 flex items-center gap-1">{end}</div>}
    </div>
  )
})

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(function Textarea(
  { invalid, className, ...rest },
  ref,
) {
  return <textarea ref={ref} aria-invalid={invalid || undefined} className={cn(control, 'min-h-20 px-3 py-2 leading-relaxed', className)} {...rest} />
})

export interface SelectOption {
  value: string | number
  label: string
  disabled?: boolean
  /** Per-option SVG; when absent it's derived from the field's kind icon and the option's label. */
  icon?: IconComponent
}

/**
 * A select whose list shows an SVG per option. A native <select> can't render icons in its options, so
 * the visible control is a Radix popover listbox, and a visually-hidden native <select> underneath
 * keeps every existing caller working unchanged: `ref`, `name`, react-hook-form's `register()`, and
 * `onChange={(e) => e.target.value}` all still hit a real <select> (the listbox sets its value and
 * fires a native change event through React's own value setter).
 */
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { options: SelectOption[]; placeholder?: string; invalid?: boolean; icon?: IconComponent }>(
  function Select({ options, placeholder, invalid, icon, className, disabled, value, defaultValue, onChange, ...rest }, ref) {
    const native = useRef<HTMLSelectElement>(null)
    useImperativeHandle(ref, () => native.current!, [])
    const [open, setOpen] = useState(false)
    const [q, setQ] = useState('')
    // Uncontrolled callers (register()) drive the native element; mirror its value so the trigger stays in sync.
    const [inner, setInner] = useState(String(defaultValue ?? value ?? ''))
    const current = value !== undefined ? String(value) : inner
    useEffect(() => { if (value !== undefined) setInner(String(value)) }, [value])
    const lookup = lookupOfIcon(icon)
    const iconOf = (o: SelectOption) => o.icon ?? iconForOption(lookup, o.label) ?? icon
    const selected = options.find((o) => String(o.value) === current)
    const searchable = options.length > 8
    const visible = useMemo(() => {
      const s = q.trim().toLowerCase()
      return s ? options.filter((o) => o.label.toLowerCase().includes(s)) : options
    }, [options, q])

    const pick = (v: string) => {
      const el = native.current
      if (el) {
        const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set
        setter ? setter.call(el, v) : (el.value = v)
        el.dispatchEvent(new Event('change', { bubbles: true }))
      }
      setInner(v)
      setOpen(false)
      setQ('')
    }

    return (
      <div className="relative">
        <select
          ref={native}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          value={value}
          defaultValue={defaultValue}
          onChange={(e) => { setInner(e.target.value); onChange?.(e) }}
          className="sr-only"
          tabIndex={-1}
          aria-hidden
          {...rest}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>
          ))}
        </select>
        <Popover.Root open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQ('') }}>
          <Popover.Trigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-invalid={invalid || undefined}
              aria-haspopup="listbox"
              aria-expanded={open}
              className={cn(control, 'flex h-10 items-center gap-2 pe-8 ps-3 text-start', (icon || selected) && 'ps-2.5', className)}
            >
              {(selected ? iconOf(selected) : icon) && <Icon icon={(selected ? iconOf(selected) : icon)!} size={15} className="text-fg-subtle" />}
              <span className={cn('min-w-0 flex-1 truncate', !selected && 'text-fg-subtle')}>{selected?.label ?? placeholder ?? ''}</span>
              <ChevronDown size={15} className={cn('pointer-events-none absolute end-2.5 top-1/2 -translate-y-1/2 text-fg-subtle transition-transform duration-200', open && 'rotate-180')} aria-hidden />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content align="start" sideOffset={4} className="select-pop z-50 w-[var(--radix-popover-trigger-width)] min-w-56 overflow-hidden rounded-xl border border-border bg-surface shadow-lg animate-scale-in" onOpenAutoFocus={(e) => { if (!searchable) e.preventDefault() }}>
              {searchable && (
                <div className="border-b border-border p-2">
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="…" className={cn(control, 'h-8 px-2.5')} autoFocus />
                </div>
              )}
              <ul role="listbox" className="max-h-72 overflow-y-auto p-1">
                {placeholder !== undefined && !q && (
                  <li role="option" aria-selected={!current} onClick={() => pick('')} className={cn('flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm text-fg-subtle hover:bg-surface-hover', !current && 'bg-surface-hover')}>
                    <span className="size-4" />{placeholder}
                  </li>
                )}
                {visible.map((o) => {
                  const OI = iconOf(o)
                  const active = String(o.value) === current
                  return (
                    <li
                      key={o.value}
                      role="option"
                      aria-selected={active}
                      aria-disabled={o.disabled || undefined}
                      onClick={() => { if (!o.disabled) pick(String(o.value)) }}
                      className={cn('select-opt flex items-center gap-2 rounded-md px-2.5 py-2 text-sm', o.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-surface-hover', active && 'bg-brand-soft font-medium')}
                    >
                      <span className={cn('flex size-6 shrink-0 items-center justify-center rounded-md', active ? 'bg-brand/10' : 'bg-bg-subtle')}>
                        {OI ? <Icon icon={OI} size={14} weight={active ? 'fill' : 'regular'} className={active ? 'keep-color text-brand' : 'text-fg-subtle'} /> : <span className="size-1.5 rounded-full bg-fg-subtle" />}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{o.label}</span>
                      {active && <Check size={14} className="keep-color shrink-0 text-brand" />}
                    </li>
                  )
                })}
                {!visible.length && <li className="px-2.5 py-3 text-center text-xs text-fg-subtle">—</li>}
              </ul>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </div>
    )
  },
)

export function Checkbox({ checked, indeterminate, onChange, label, disabled, className }: { checked: boolean; indeterminate?: boolean; onChange: (v: boolean) => void; label?: ReactNode; disabled?: boolean; className?: string }) {
  return (
    <label className={cn('inline-flex cursor-pointer select-none items-center gap-2 text-sm', disabled && 'cursor-not-allowed opacity-60', className)}>
      <button
        type="button"
        role="checkbox"
        aria-checked={indeterminate ? 'mixed' : checked}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation()
          onChange(!checked)
        }}
        className={cn(
          'flex size-[18px] shrink-0 items-center justify-center rounded-[5px] border transition-colors',
          checked || indeterminate ? 'border-brand bg-brand text-white' : 'border-border-strong bg-surface hover:border-fg-subtle',
        )}
      >
        {indeterminate ? <span className="h-0.5 w-2 rounded bg-white" /> : checked ? <Check size={12} aria-hidden /> : null}
      </button>
      {label}
    </label>
  )
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; disabled?: boolean }) {
  return (
    <label className={cn('inline-flex cursor-pointer select-none items-center gap-2.5 text-sm', disabled && 'cursor-not-allowed opacity-60')}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn('relative h-5 w-9 shrink-0 rounded-full transition-colors', checked ? 'bg-brand' : 'bg-border-strong')}
      >
        <span className={cn('absolute top-0.5 size-4 rounded-full bg-white shadow-sm transition-[inset-inline-start] duration-150', checked ? 'start-[18px]' : 'start-0.5')} />
      </button>
      {label}
    </label>
  )
}

export function SearchInput({ value, onChange, placeholder, className, autoFocus }: { value: string; onChange: (v: string) => void; placeholder?: string; className?: string; autoFocus?: boolean }) {
  return (
    <Input
      icon={Search}
      value={value}
      autoFocus={autoFocus}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={className}
      suffix={
        value ? (
          <button type="button" onClick={() => onChange('')} className="rounded p-1 text-fg-subtle hover:text-fg" aria-label="Clear">
            <X size={14} aria-hidden />
          </button>
        ) : null
      }
    />
  )
}
